import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';

type CustomizableTokens =
  | '--wui-color-backdrop'
  | '--wui-color-bg-app'
  | '--wui-color-bg-fill-active'
  | '--wui-color-bg-fill-hover'
  | '--wui-color-bg-fill-white '
  | '--wui-color-bg-fill'
  | '--wui-color-bg-surface-active'
  | '--wui-color-bg-surface-disabled'
  | '--wui-color-bg-surface-hover'
  | '--wui-color-bg-surface-secondary-active'
  | '--wui-color-bg-surface-secondary-hover'
  | '--wui-color-bg-surface-secondary'
  | '--wui-color-bg-surface-selected-active'
  | '--wui-color-bg-surface-selected-hover'
  | '--wui-color-bg-surface-selected'
  | '--wui-color-bg-surface-tertiary'
  | '--wui-color-bg-surface'
  | '--wui-color-bg-tooltip'
  | '--wui-color-border-active-selected'
  | '--wui-color-border-active'
  | '--wui-color-border-disabled'
  | '--wui-color-border-hover-selected'
  | '--wui-color-border-hover'
  | '--wui-color-border-secondary-active'
  | '--wui-color-border-secondary-hover'
  | '--wui-color-border-secondary'
  | '--wui-color-border-selected'
  | '--wui-color-border'
  | '--wui-color-drop-shadow'
  | '--wui-color-focus-color'
  | '--wui-color-focus-ring-disabled'
  | '--wui-color-focus-ring'
  | '--wui-color-icon-disabled'
  | '--wui-color-icon-on-fill'
  | '--wui-color-icon-selected'
  | '--wui-color-icon'
  | '--wui-color-invalid-indicator'
  | '--wui-color-notification-pill-color'
  | '--wui-color-segmented-control-checked-background'
  | '--wui-color-text-button'
  | '--wui-color-text-disabled'
  | '--wui-color-text-link'
  | '--wui-color-text-on-fill-white-selected'
  | '--wui-color-text-on-fill'
  | '--wui-color-text-secondary'
  | '--wui-color-text-selected'
  | '--wui-color-text'
  | '--wui-typography-family-brand'
  | '--wui-typography-family-default'
  | '--wui-typography-family-mono'
  | '--wui-typography-weight-body-bold'
  | '--wui-typography-weight-body'
  | '--wui-typography-weight-heading'
  | '--wui-typography-weight-label-bold'
  | '--wui-typography-weight-label';

type StyledCustomizableThemeWrapperProps = {
  /* add styled-component props here */
  children: ReactNode;
  $overrides: Partial<Record<CustomizableTokens, number | string>>;
};

const StyledCustomizableThemeWrapper = styled.div<StyledCustomizableThemeWrapperProps>(
  (props) => props.$overrides,
);

export type CustomizableThemeWrapperProps = ComponentPropsWithoutRef<'div'> & {
  children: ReactNode;
  overrides: Partial<Record<CustomizableTokens, number | string>>;
};

/**
 * Used for overriding the default theme tokens with custom values.
 * This should only be used when the ui systems team has been consulted first.
 */
export const CustomizableThemeWrapper = ({
  children,
  overrides,
  ...props
}: CustomizableThemeWrapperProps): JSX.Element => (
  <StyledCustomizableThemeWrapper
    {...props}
    $overrides={overrides}
    data-wui-theme="true"
  >
    {children}
  </StyledCustomizableThemeWrapper>
);

CustomizableThemeWrapper.displayName = 'CustomizableThemeWrapper';
